import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api, type RunPayloads } from "../lib/api";
import { Code } from "../components/primitives/Code";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { PageHeader } from "../components/primitives/PageHeader";
import { TechnicalDetails } from "../components/primitives/TechnicalDetails";
import { useConfirm } from "../components/primitives/Confirm";
import { PageHelp } from "../components/PageHelp";
import { runTone, isTerminal } from "../lib/run-status";
import { runStatusLabel } from "../lib/display-labels";

// One run (spec §6.3). SP1 changes only the header, the re-run gate and where
// raw JSON lives; the plain-language failure explanation is SP2.

interface Activity {
  type: string;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  status: "scheduled" | "started" | "success" | "fail";
  failure?: { message: string; stackTrace?: string } | null;
}

// The data-lineage stages, in pipeline order. `stageKey` matches the
// `failedStage` values the worker records (parse|filter|transform|lookup|deliver).
const LINEAGE_STAGES: Array<{ stageKey: string; label: string; field: keyof RunPayloads }> = [
  { stageKey: "parse", label: "INPUT EVENT", field: "inputEvent" },
  { stageKey: "transform", label: "TRANSFORM OUTPUT", field: "transformOutput" },
  { stageKey: "lookup", label: "LOOKUP SNAPSHOT", field: "lookupSnapshot" },
  { stageKey: "deliver", label: "DELIVER RESULT", field: "deliverResult" },
];

/**
 * One run, end to end: what it was, where it got to, and its data lineage.
 *
 * This is the page an escalation lands on, so it holds both audiences at once
 * — the plain-language account above, and the payloads inside
 * `TechnicalDetails` below.
 */
export function Run() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const confirm = useConfirm();
  const q = usePolling(() => api.getSyncLogRow(id!), 5000);
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | undefined>();
  const [payloads, setPayloads] = useState<RunPayloads | null>(null);
  const [payloadsLoaded, setPayloadsLoaded] = useState(false);
  const [payloadsError, setPayloadsError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getRunActivities(id).then((r) => {
      setActivities(r.activities);
      setWorkflowId(r.workflowId);
      if (r.error) setActivitiesError(r.error);
    }).catch((err) => setActivitiesError(err instanceof Error ? err.message : String(err)));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.getRunPayloads(id)
      .then((r) => setPayloads(r.payloads))
      .catch((err) => setPayloadsError(err instanceof Error ? err.message : String(err)))
      .finally(() => setPayloadsLoaded(true));
  }, [id]);

  if (q.status === "loading" || !q.data) return <Pulse />;
  // The route returns `{ entry }`; tolerate the legacy `{ row }`/flat shapes too.
  const data = q.data as { entry?: Record<string, any>; row?: Record<string, any> } & Record<string, any>;
  const r = (data.entry ?? data.row ?? data) as Record<string, any>;

  /** Confirm and send this event through its pipeline again, then open the run
   *  that came back so the operator lands on the thing they just started. */
  async function rerun() {
    if (!id) return;
    // An object, not a `let`: TypeScript does not see assignments made inside
    // the onConfirm closure, so a plain variable would narrow to `null` here.
    const created: { id: string | null } = { id: null };
    // Says "may write" rather than "will deliver again": a re-run is reachable
    // from any finished run, including a skipped one that never reached the
    // destination in the first place. What it will do depends on today's settings.
    const ok = await confirm({
      tier: 1,
      title: "Re-run this event?",
      body: "Anansi will process this event again with the pipeline's current settings. The record in the destination may be created or updated.",
      confirmLabel: "Re-run event",
      onConfirm: async () => {
        const { syncLogId } = await api.rerunRun(id);
        created.id = syncLogId;
      },
    });
    // No new run id (e.g. the original event was already reprocessed) —
    // usePolling will pick up any status change on the current run.
    if (ok && created.id) nav(`/activity/${created.id}`);
  }

  // Any FINISHED run can be re-run — the API's eligibility rule is the same
  // "is it terminal" question (RERUNNABLE_STATUSES in apps/api/src/routes/sync-log.ts).
  // A skipped or conflicted run is a decision taken against the config and
  // upstream data of the moment, and re-running it is how an operator applies a
  // later change to it. Only in-flight runs are withheld, so a replay cannot
  // race its own delivery.
  const canRerun = isTerminal(r.status);

  return (
    <div>
      <PageHeader
        eyebrow="Run"
        title={runStatusLabel(r.status)}
        meta={r.id}
        summary={
          r.rerunOf || (r.attempt && r.attempt > 1) ? (
            <span className="mono">
              {r.rerunOf ? (
                <button type="button" className="run-link run-link--button" onClick={() => nav(`/activity/${r.rerunOf}`)}>
                  re-run of {r.rerunOf}
                </button>
              ) : null}
              {r.rerunOf && r.attempt && r.attempt > 1 ? " · " : null}
              {r.attempt && r.attempt > 1 ? <span>attempt {r.attempt}</span> : null}
            </span>
          ) : undefined
        }
        actions={
          <>
            <Badge tone={runTone(r.status)}>{runStatusLabel(r.status)}</Badge>
            {canRerun ? (
              <Button variant="secondary" onClick={() => void rerun()}>
                Re-run
              </Button>
            ) : null}
          </>
        }
      />
      <PageHelp path="/activity/:id" />

      <div className="run-detail">
        <div className="run-detail__timeline">
          <div className="uppercase-label" style={{ marginBottom: 8 }}>ACTIVITY TIMELINE</div>
          {activities === null && !activitiesError && <p className="page-help">Loading activities…</p>}
          {activitiesError && (
            <p className="page-help">Activity timeline unavailable: {activitiesError}</p>
          )}
          {activities?.length === 0 && !activitiesError && (
            <p className="page-help">No activity events recorded for this workflow.</p>
          )}
          {activities?.map((a, idx) => (
            <ActivityRow key={idx} activity={a} />
          ))}
        </div>

        <div className="run-detail__metadata">
          <div className="uppercase-label" style={{ marginBottom: 8 }}>METADATA</div>
          <dl>
            <dt>pipeline</dt><dd className="mono">{r.pipelineId}</dd>
            <dt>event id</dt><dd className="mono">{r.eventId}</dd>
            <dt>workflow id</dt><dd className="mono">{workflowId ?? r.workflowId ?? "—"}</dd>
            <dt>workflow run id</dt><dd className="mono">{r.workflowRunId ?? "—"}</dd>
            <dt>received at</dt><dd className="mono">{r.receivedAt ?? "—"}</dd>
            <dt>started at</dt><dd className="mono">{r.startedAt ?? "—"}</dd>
            <dt>completed at</dt><dd className="mono">{r.completedAt ?? "—"}</dd>
            <dt>processing ms</dt><dd className="mono">{r.processingTimeMs ?? "—"}</dd>
            <dt>source environment</dt>
            <dd className="mono">
              {r.producingAccount
                ? `${r.producingAccount}${r.environmentToken ? ` · ${r.environmentToken}` : ""}`
                : "—"}
            </dd>
            {r.skipReason && (<><dt>skip reason</dt><dd>{r.skipReason}</dd></>)}
            {r.errorMessage && (<><dt>error</dt><dd>{r.errorMessage}</dd></>)}
          </dl>
        </div>
      </div>

      <Lineage payloads={payloads} loaded={payloadsLoaded} error={payloadsError} />
    </div>
  );
}

/**
 * What the record looked like at each stage: the event in, the transformed
 * payload, the destination snapshot, the delivery result.
 *
 * `loaded` and `error` are separate from `payloads` on purpose — "still
 * loading", "could not load" and "this run captured none" are three different
 * answers, and collapsing them would let a failed fetch read as a run that
 * never recorded anything.
 */
export function Lineage({
  payloads,
  loaded,
  error,
}: {
  payloads: RunPayloads | null;
  loaded: boolean;
  error: string | null;
}) {
  return (
    <div className="lineage">
      <div className="uppercase-label" style={{ marginBottom: 8 }}>DATA LINEAGE</div>
      {!loaded && !error && <p className="page-help">Loading lineage…</p>}
      {error && <p className="page-help">Lineage unavailable: {error}</p>}
      {loaded && !error && !payloads && (
        <p className="page-help">No lineage captured for this run.</p>
      )}
      {payloads && (
        <>
          {payloads.failedStage && (
            <p className="lineage__failed-banner">
              Failed at stage <span className="mono">{payloads.failedStage}</span>
            </p>
          )}
          <div className="lineage__flow">
            {LINEAGE_STAGES.map((stage, idx) => (
              <LineageStage
                key={stage.stageKey}
                label={stage.label}
                value={payloads[stage.field]}
                failed={payloads.failedStage === stage.stageKey}
                showEdge={idx > 0}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** One stage of the lineage: its payload, collapsed, with the edge that joins
 *  it to the stage before and a marker when this is where the run stopped. */
function LineageStage({
  label,
  value,
  failed,
  showEdge,
}: {
  label: string;
  value: unknown;
  failed: boolean;
  showEdge: boolean;
}) {
  const present = value !== null && value !== undefined;
  const json = present ? JSON.stringify(value, null, 2) : "";
  return (
    <>
      {showEdge && <div className="lineage__edge" aria-hidden="true" />}
      <div className={`lineage__stage${failed ? " lineage__stage--failed" : ""}`}>
        <div className="lineage__stage-head">
          <span className="lineage__stage-label">{label}</span>
          <Badge tone={failed ? "fail" : present ? "ok" : "muted"}>
            {failed ? "FAILED" : present ? "CAPTURED" : "EMPTY"}
          </Badge>
        </div>
        {present && (
          <TechnicalDetails copyText={json}>
            <Code block>{json}</Code>
          </TechnicalDetails>
        )}
      </div>
    </>
  );
}

/** One workflow step: what ran, how long it took, and its failure if it had one. */
function ActivityRow({ activity }: { activity: Activity }) {
  const start = activity.startedAt ?? activity.scheduledAt;
  const end = activity.completedAt;
  const ms =
    start && end ? new Date(end).getTime() - new Date(start).getTime() : undefined;
  const errorText = activity.failure ? (activity.failure.stackTrace ?? activity.failure.message) : null;
  return (
    <div className="run-detail__activity">
      <span className={`run-detail__activity-marker${activity.status === "fail" ? " run-detail__activity-marker--fail" : ""}`} />
      <div>
        <div className="run-detail__activity-name">{activity.type}</div>
        <div className="run-detail__activity-meta">
          {activity.status} · {start ?? "—"}{ms !== undefined ? ` · ${ms}ms` : ""}
        </div>
        {errorText && (
          <TechnicalDetails title="Error details" copyText={errorText}>
            <Code block>{errorText}</Code>
          </TechnicalDetails>
        )}
      </div>
      <Badge tone={activity.status === "success" ? "ok" : activity.status === "fail" ? "fail" : "muted"}>
        {activity.status.toUpperCase()}
      </Badge>
    </div>
  );
}
